/**
 * The text to show a user for a failed `apiFetch` call (MAR-948 wave 1). One order, for every caller:
 *
 *   1. `err.data.message` — the server's own text. Nitro's JSON error body carries `message` =
 *      h3's `createError` `message`, which h3 fills from `statusMessage` when a handler set only
 *      that. So this is the most specific human text the server sends, whichever field the handler
 *      used. (`data.statusMessage` is deliberately NOT read: it is never more specific, and when a
 *      handler set only `message` — `requireAdmin`, `parseBody`'s 422, most of `server/` — Nitro
 *      fills it with the placeholder `'Server Error'`.)
 *      EXCEPT Nitro's placeholder: for an unhandled error in production Nitro replaces the message
 *      with exactly `'Server Error'` (`runtime/internal/error/prod.mjs`). That is English filler, not a
 *      message, so it counts as none and the caller's translated fallback shows. Only the exact
 *      string is skipped; a real message that merely contains those words is shown as-is.
 *   2. `err.message` — but only for an error the app threw itself. An ofetch `FetchError`'s message
 *      is diagnostic (`[POST] "/api/x": <no response> Failed to fetch`, `… 502 Bad Gateway` for a
 *      proxy's HTML page), which belongs in the console, not in front of a user.
 *   3. `fallback` — the caller's own, usually i18n, text.
 *
 * The wire shapes this was chosen against are pinned in `tests/unit/apiErrorMessage.test.ts`. The
 * hand-rolled `err?.data?.message || …` chains it replaced read those fields in five different
 * orders; `tests/unit/apiErrorMessageGuard.test.ts` fails if one comes back anywhere in `app/`.
 */
const NITRO_UNHANDLED_PLACEHOLDER = 'Server Error'

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null
}

export function apiErrorMessage(err: unknown, fallback: string): string {
  if (!err || typeof err !== 'object') return fallback
  const e = err as { name?: unknown, message?: unknown, data?: unknown }
  const body = e.data && typeof e.data === 'object' ? e.data as { message?: unknown } : null
  const serverMessage = nonEmptyString(body?.message)
  return (serverMessage === NITRO_UNHANDLED_PLACEHOLDER ? null : serverMessage)
    ?? (e.name === 'FetchError' ? null : nonEmptyString(e.message))
    ?? fallback
}
